import { Channel, ChannelConnectParams, ChannelStatus, ChannelStatusListener } from 'core/models/channel';
import { MediaDevice } from 'core/models/mediaDevice';
import { MediaDeviceApiCall, MediaDeviceEndpointResponse, SoraEndpoint, JanusEndpoint } from 'infrastructure/t4/api/MediaDevice';
import { getRecordableConnector, getStatsReportable, MediaStreamConnector, RecordableConnector } from '../../connectors/MediaStreamConnector';
import { SoraConnector } from '../../connectors/SoraConnector';
import { MediaDeviceStatusListener, MediaDeviceRepository } from 'core/repositories/MediaDeviceRepository';
import { isStatusObservable, ConnectionStatusListener, ConnectionStatus } from '../../connectors/StatusObservableConnector';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { JanusVideoRoomConnector } from 'infrastructure/connectors/JanusVideoRoomConnector';
import { isReconnectable } from 'infrastructure/connectors/ReconnectableConnector';
import { once } from 'infrastructure/utility/once';
import { LatencyData } from 'core/models/latency';
import { isLatencyMeasurable, TimestampDecodingConnector } from 'infrastructure/connectors/TimestampDecodingConnector';

function createSoraConnector(endpoint: SoraEndpoint): MediaStreamConnector {
    return new SoraConnector({
        signalingServer: endpoint.uri || 'wss://devdrive.tier4.jp/signaling',
        roomName: endpoint.sora_metadata.channel_id,
        token: endpoint.sora_metadata.token,
        expire: Date.parse(endpoint.sora_metadata.token_expires_at),
    });
}

function createJanusConnector(endpoint: JanusEndpoint, useInsertableStream: boolean): MediaStreamConnector {
    switch (endpoint.janus_metadata.plugin) {
        case 'videoroom':
            return new JanusVideoRoomConnector({
                signalingServer: endpoint.uri,
                room: endpoint.janus_metadata.videoroom.room,
                username: endpoint.janus_metadata.videoroom.username,
                config: {
                    iceServers: endpoint.janus_metadata.iceServers,
                    encodedInsertableStreams: useInsertableStream,
                },
            });
    }
}

function dispatchConnectorByServer(json: MediaDeviceEndpointResponse, useInsertableStream: boolean): MediaStreamConnector | (MediaStreamConnector & RecordableConnector) {
    if (!json.endpoint) {
        throw new Error(`unexpected endpoint response: ${JSON.stringify(json)}`);
    }

    switch (json.endpoint.type) {
        case 'SORA':
            return createSoraConnector(json.endpoint);

        case 'JANUS':
            return createJanusConnector(json.endpoint, useInsertableStream);

        default:
            throw new Error(`unsupported endpoint type: ${json.endpoint.type ?? '(no type)'}`);
    }
}

function dispatchConnector(json: MediaDeviceEndpointResponse, useInsertableStream: boolean): MediaStreamConnector | (MediaStreamConnector & RecordableConnector) {
    const connector = dispatchConnectorByServer(json, useInsertableStream);

    if (useInsertableStream) {
        return new TimestampDecodingConnector(connector);
    } else {
        return connector;
    }
}

export class T4APIChannel implements Channel {
    readonly mediaDeviceId: MediaDevice.Id;
    readonly projectId: Project.Id;
    readonly environmentId: Environment.Id;
    readonly apiCall: MediaDeviceApiCall;
    readonly mediaDeviceRepository: MediaDeviceRepository;
    private _currentStatus: ChannelStatus | undefined;
    private readonly useInsertableStream: boolean = true;

    get currentStatus(): ChannelStatus | undefined {
        return this._currentStatus;
    }

    private connector: MediaStreamConnector | (MediaStreamConnector & RecordableConnector) | undefined;

    getConnector() { return this.connector; }

    private connectionStatus: ConnectionStatus | undefined;
    private readonly handleStatus: ConnectionStatusListener = status => {
        this.connectionStatus = status;
    };

    constructor(camera: MediaDevice, projectId: Project.Id, environmentId: Environment.Id, apiCall: MediaDeviceApiCall, mediaDeviceRepository: MediaDeviceRepository) {
        this.mediaDeviceId = camera.id;
        this.projectId = projectId;
        this.environmentId = environmentId;
        this.apiCall = apiCall;
        this.mediaDeviceRepository = mediaDeviceRepository;
    }

    async prepare(): Promise<void> {
        await this._preparing();
    }

    protected async createConnector() {
        const mediaDeviceId = this.mediaDeviceId;
        const json = await this.apiCall<MediaDeviceEndpointResponse>(`/v1/projects/${this.projectId}/environments/${this.environmentId}/devices/${mediaDeviceId}/endpoint`);
        const connector = dispatchConnector(json, this.useInsertableStream);
        return connector;
    }

    private _preparing = once(async () => {
        this.connector = await this.createConnector();
        if (isStatusObservable(this.connector)) {
            this.connector.addStatusListener(this.handleStatus);
        }
        if (isReconnectable(this.connector)) {
            this.connector.addReconnectListener((connection) => {
                if (!this.connecting) {
                    console.warn(`reconnect stream but not attempting to connect: ${this.mediaDeviceId}`);
                }
                const stream = connection.stream;
                this._reconnectionListeners.forEach(listener => listener(stream));
            });
        }
    });

    private connecting = false;

    async connect(params: ChannelConnectParams): Promise<MediaStream> {
        if (!this.connector) {
            await this.prepare();
        }
        if (!this.connector) throw new Error('PANIC!');

        this.connecting = true;
        const { stream } = await this.connector.connect(params);
        this.connecting = false;

        return stream;
    }

    async disconnect() {
        if (this.connector) {
            if (isStatusObservable(this.connector)) {
                this.connector.removeStatusListener(this.handleStatus);
                this.connectionStatus = undefined;
            }
            this.connector.disconnect();
            this.connector = undefined;
        }
    }

    get isRecordable(): boolean {
        const connector = this.connector;
        if (!connector) return false;
        const recordingConnector = getRecordableConnector(connector);
        if (!recordingConnector) return false;
        return true;
    }

    async enableRecording(enabled: boolean) {
        const connector = this.connector;
        if (connector) {
            const recordingConnector = getRecordableConnector(connector);
            if (recordingConnector) {
                await recordingConnector.enableRecording(enabled);
            } else {
                console.error('recording not supported');
            }
        } else {
            throw new Error('not connected yet');
        }
    }

    addStatusListener(listener: ChannelStatusListener): void {
        this._listenerManager.addListener(listener);
        if (this._currentStatus !== undefined) {
            listener(this._currentStatus);
        }
    }

    removeStatusListener(listener: ChannelStatusListener): void {
        this._listenerManager.removeListener(listener);
    }

    private readonly _listenerManager = createListenerManager<ChannelStatusListener>(listeners => {
        const listener: MediaDeviceStatusListener = deviceStatus => {
            const publishing = this.connectionStatus?.publishing ?? deviceStatus.started;
            const resolution = deviceStatus.resolution;

            const state = { publishing, resolution };
            this._currentStatus = state;

            [...listeners].forEach(listener => {
                listener(state);
            });
        };

        this.mediaDeviceRepository.addStatusListener(this.mediaDeviceId, this.projectId, listener);

        return () => {
            this.mediaDeviceRepository.removeStatusListener(this.mediaDeviceId, this.projectId, listener);
            this._currentStatus = undefined;
        };
    });

    private readonly _reconnectionListeners = createListenerManager<(stream: MediaStream) => void>();

    addReconnectListener(listener: (stream: MediaStream) => void): void {
        this._reconnectionListeners.addListener(listener);
    }

    removeReconnectListener(listener: (stream: MediaStream) => void): void {
        this._reconnectionListeners.removeListener(listener);
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        if (!this.connector) {
            throw new Error(`not connected yet ${this.mediaDeviceId}`);
        }

        const reportable = getStatsReportable(this.connector);
        if (reportable) {
            return await reportable.getStats(selector);
        } else {
            console.error('getStats not supported');
            return undefined;
        }
    }

    getLatency(): LatencyData | undefined {
        if (this.connector && isLatencyMeasurable(this.connector)) {
            return this.connector.getLatency();
        }
        return undefined;
    }
}
